import { FC, Fragment, ReactNode } from 'react'
import styles from './richText.module.scss'

// Minimal markdown renderer for the structured fields the AI Listening backend
// emits (observation / whyItMatters / likelyDriver / suggestedExecution). It
// handles only **bold** and "- " bullets — the subset that backend actually
// produces. Deliberately scoped to those fields: the legacy `details` string
// used by every other module keeps rendering as plain text, so nothing changes
// for Social Listening, Ad Library, Inbox, Content Analysis or Review Analysis.

const BOLD_PATTERN = /\*\*(.+?)\*\*/g

// Splits a line into plain text and <strong> runs.
const renderInline = (line: string): ReactNode[] => {
  const nodes: ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  // A fresh regex per call — /g regexes carry lastIndex between calls.
  const pattern = new RegExp(BOLD_PATTERN)

  while ((match = pattern.exec(line)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(line.slice(lastIndex, match.index))
    }
    nodes.push(<strong key={`b-${match.index}`}>{match[1]}</strong>)
    lastIndex = match.index + match[0].length
  }
  if (lastIndex < line.length) {
    nodes.push(line.slice(lastIndex))
  }
  return nodes.length > 0 ? nodes : [line]
}

const isBullet = (line: string) => /^\s*[-*]\s+/.test(line)
const stripBullet = (line: string) => line.replace(/^\s*[-*]\s+/, '')

export const RichText: FC<{ text: string }> = ({ text }) => {
  if (!text) return null

  const lines = text.split('\n')
  const blocks: ReactNode[] = []
  let bullets: string[] = []

  const flushBullets = (key: string) => {
    if (bullets.length === 0) return
    blocks.push(
      <ul className={styles.list} key={`ul-${key}`}>
        {bullets.map((bullet, i) => (
          <li key={i}>{renderInline(bullet)}</li>
        ))}
      </ul>
    )
    bullets = []
  }

  lines.forEach((line, i) => {
    if (isBullet(line)) {
      bullets.push(stripBullet(line))
      return
    }
    flushBullets(String(i))
    if (line.trim() === '') return
    blocks.push(
      <p className={styles.paragraph} key={`p-${i}`}>
        {renderInline(line)}
      </p>
    )
  })
  flushBullets('end')

  return <Fragment>{blocks}</Fragment>
}
